import { useCallback } from "react";
import { TFormHealth } from "..";
import useMyMontepioContext from "../../../components/Context/useMyMontepioContext";
import { MGText } from "../../../designSystem/components/MGText";
import { MGButton } from "../../../designSystem/components/MGButton";
import styles from "../Health.module.css";
import { TPdfViewerDocument } from "../../PdfViewer";
import { THealthFormContent } from "../api/types";
import { UmbracoContent } from "../../../components/UmbracoContent";
import { isDev } from "../../../env";
import { BackgroundForm } from "../../../components/BackgroundForm";

type TTermsStepProps = {
  formHealth: TFormHealth;
  content: THealthFormContent;
  readDocumentIds: string[];
  readCheckboxesIds: string[];
  handleCheckboxChange: (id: string) => void;
  onContinue: (formHealth: TFormHealth) => void;
  onOpenPdf: (document: TPdfViewerDocument) => void;
  onPdfEnd: (pdfId: string) => void;
};

const STATUTES_DOCUMENT_URL = `${process.env.PUBLIC_URL}/pdfs/estatutos.pdf`;

export const TermsStep = ({
  formHealth,
  content,
  readDocumentIds,
  readCheckboxesIds,
  handleCheckboxChange,
  onContinue,
  onOpenPdf,
  onPdfEnd,
}: TTermsStepProps) => {
  const { isMobileAuth } = useMyMontepioContext();

  const handleOpenRequiredPdf = useCallback(
    (document: TPdfViewerDocument, hasBottomDrawer = false) => {
      const isSelected = readDocumentIds.includes(document.id);

      onOpenPdf({
        ...document,
        hasBottomDrawer: hasBottomDrawer && !isSelected,
        onPdfEnd,
      });
    },
    [onOpenPdf, onPdfEnd, readDocumentIds],
  );

  const allDocuments = formHealth.supplementaryHealthPlan!
    ? [...content.documentos.documents, ...content.documentosPlano.documents]
    : content.documentos.documents;

  const allCheckboxes = formHealth.supplementaryHealthPlan!
    ? [...content.termosECondicoes.checkboxes, ...content.termosECondicoesPlano.checkboxes]
    : content.termosECondicoes.checkboxes;

  const allDocumentsRead = allDocuments.every((doc) => readDocumentIds.includes(doc.id));

  const allCheckboxesRead = allCheckboxes.every((item) => readCheckboxesIds.includes(item.id));

  const onDone = useCallback(() => {
    onContinue({ ...formHealth });
  }, [formHealth, onContinue]);

  return (
    <div style={{ display: "flex", flex: 1, flexDirection: "column" }}>
      <MGText textStyleDefinition="body-100-03" style={{ marginTop: 16 }}>
        Para continuar é necessário ler e aceitar a seguinte informação:
      </MGText>

      <BackgroundForm style={{ marginTop: isMobileAuth ? 48 : 24 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 48 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 48 }}>
            <UmbracoContent
              data={[content.termosECondicoes]}
              selectedCheckboxIds={readCheckboxesIds}
              onCheckboxChange={(id) => handleCheckboxChange(id)}
            />

            {formHealth.supplementaryHealthPlan! && (
              <UmbracoContent
                data={[content.termosECondicoesPlano]}
                selectedCheckboxIds={readCheckboxesIds}
                onCheckboxChange={(id) => handleCheckboxChange(id)}
              />
            )}
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 16,
              marginBottom: isMobileAuth ? 48 : 0,
            }}>
            <UmbracoContent
              data={[content.documentos]}
              readDocumentIds={readDocumentIds}
              documentAppearance="white"
              onDocumentClick={(document) =>
                handleOpenRequiredPdf(
                  {
                    id: document.id,
                    title: document.name,
                    url: isDev ? STATUTES_DOCUMENT_URL : `${process.env.PUBLIC_URL}${document.url}`,
                  },
                  true,
                )
              }
            />

            {formHealth.supplementaryHealthPlan! && (
              <UmbracoContent
                data={[content.documentosPlano]}
                readDocumentIds={readDocumentIds}
                documentAppearance="white"
                onDocumentClick={(document) =>
                  handleOpenRequiredPdf(
                    {
                      id: document.id,
                      title: document.name,
                      url: isDev
                        ? STATUTES_DOCUMENT_URL
                        : `${process.env.PUBLIC_URL}${document.url}`,
                    },
                    true,
                  )
                }
              />
            )}
          </div>
        </div>
      </BackgroundForm>

      <MGButton
        disabled={!allDocumentsRead || !allCheckboxesRead}
        fullWidth={isMobileAuth}
        className={`${styles.buttonFooter} ${isMobileAuth ? styles.buttonFooterFixed : ""}`}
        onClick={onDone}>
        {"Continuar"}
      </MGButton>
    </div>
  );
};
